/**
 * Environment Variable Validation
 *
 * Validates that all required environment variables are present at startup.
 * Fails fast with a clear error message rather than producing cryptic
 * runtime failures deep in OIDC or GraphQL initialization.
 *
 * This runs once at application boot before any React rendering.
 */

import { getEnv, isMockMode } from '@rtvloco/platform/env';

/** Environment variables that must be present for the application to function */
const REQUIRED_VARS: ReadonlyArray<{ key: string; description: string }> = [
  {
    key: 'VITE_INSPECTOR_GRAPHQL_URL',
    description: 'Inspector GraphQL HTTP endpoint (https://...)',
  },
  {
    key: 'VITE_OIDC_AUTHORITY',
    description: 'OIDC Authority URL (Keycloak realm endpoint)',
  },
  {
    key: 'VITE_OIDC_CLIENT_ID',
    description: 'OIDC Client ID registered in Keycloak',
  },
];

/**
 * Validates that all required environment variables are set.
 *
 * @throws Error listing all missing variables when any are absent.
 *         The error message is intentionally verbose to aid debugging
 *         in K8s pod logs during initial deployment.
 */
export function validateRequiredEnv(): void {
  if (isMockMode) return;
  const missing: string[] = [];

  for (const { key, description } of REQUIRED_VARS) {
    const value = getEnv(key);
    if (value === undefined || value.trim() === '') {
      missing.push(`  - ${key}: ${description}`);
    }
  }

  if (missing.length > 0) {
    const message = [
      '[Inspector] Missing required environment variables:',
      ...missing,
      '',
      'Set them in .env.local (Vite) or window.__ENV__ (Helm runtime config).',
      'See docs/frontend/README.md for configuration details.',
    ].join('\n');

    throw new Error(message);
  }
}
